import isEmpty from "lodash/isEmpty";
import jwt_decode from "jwt-decode";

import { adminRequests } from "@/api/(requests)/admin";
import { authRequests } from "@/api/(requests)/auth";
import { isProvider } from "@/utils/authToken";
import { parseErrorMessage } from "@/lib/helpers/parseErrorMessage";
import { providerRequests } from "@/api/(requests)/provider";
import { sharedRequests } from "@/api/(requests)/shared";
import { TOAST_ALERT_2, TOAST_SUCCESS, TOAST_SUCCESS_2 } from "@/components/UI/Alert";
import { uploadInsuranceImageFile, UserFileTypes } from "@/utils/fileUtil";

/*
 * The base route for the logged in user is decided in logIn action.
 * The ROLES_CONF file contains the config for setting base route
 * eg. if role is provider:pcp then base route will be /provider/*
 */
import ROLES_CONF from "../../config/roles";

import * as actionTypes from "./actionTypes";
import { events, logOut, resetSessionTimeout } from "./logout";
import { showAlert } from "./alerts";

const authSuccess = (
  token,
  refreshToken,
  accessToken,
  redirectPath,
  role,
  daysUntilPasswordReset,
) => {
  return {
    accessToken,
    daysUntilPasswordReset: daysUntilPasswordReset,
    idToken: token,
    redirectPath: redirectPath,
    refreshToken,
    role: role,
    type: actionTypes.AUTH_SUCCESS,
  };
};

const pwResetRequired = (cleanResponse) => {
  let idToken = cleanResponse.AuthenticationResult.IdToken;
  let refreshToken = cleanResponse.AuthenticationResult.RefreshToken;
  let accessToken = cleanResponse.AuthenticationResult.AccessToken;

  return {
    accessToken,
    errorType: "PasswordResetRequired",
    idToken: idToken,
    refreshToken,
    type: actionTypes.PW_RESET_REQUIRED,
  };
};

const authFail = (error, errorType) => {
  return {
    error: error,
    errorType,
    type: actionTypes.AUTH_FAIL,
  };
};

const authChallenge = (response) => {
  return {
    redirectPath: "/auth/challenge",
    response: response,
    type: actionTypes.AUTH_CHALLENGE,
  };
};

export const profileFetchSuccess = (profileData) => {
  return {
    profile: profileData,
    type: actionTypes.PROFILE_FETCH_SUCCESS,
  };
};

const profileFetchFail = () => {
  return {
    type: actionTypes.PROFILE_FETCH_FAIL,
  };
};

const referralSuccess = (formData) => {
  return {
    formData: formData,
    type: actionTypes.REFERRAL_SUCCESS,
  };
};
const referralFail = () => {
  return {
    type: actionTypes.REFERRAL_FAIL,
  };
};

const signUpSuccess = () => {
  return {
    type: actionTypes.SIGNUP_SUCCESS,
  };
};
const signUpFail = () => {
  return {
    type: actionTypes.SIGNUP_FAIL,
  };
};

const getToken = (token, refreshToken, accessToken, redirectPath, role) => {
  return {
    accessToken: accessToken,
    idToken: token,
    redirectPath: redirectPath,
    refreshToken: refreshToken,
    role: role,
    type: actionTypes.LOAD_TOKEN,
  };
};

export const updateJWT = ({ accessToken, refreshToken, token }) => {
  return {
    accessToken,
    refreshToken,
    token,
    type: actionTypes.UPDATE_JWT,
  };
};

export const DEFAULT_SESSION_TIMEOUT = 60; // in minutes

const initSessionTimeout =
  (sessionTimeoutMinutes = DEFAULT_SESSION_TIMEOUT) =>
  (dispatch) => {
    // Remove stale timer-* keys from previous sessions (e.g. closed tabs, crashes)
    // that were never cleaned up, preventing indefinite accumulation in localStorage.
    Object.keys(localStorage)
      .filter((key) => key.startsWith("timer-"))
      .forEach((key) => localStorage.removeItem(key));

    const sessionTimerId = "timer-" + new Date().getTime();

    localStorage.setItem(sessionTimerId, "0");
    resetSessionTimeout();
    const sessionTimer = setInterval(() => {
      const elapsedTime = parseInt(localStorage.getItem(sessionTimerId)) + 1;

      localStorage.setItem(sessionTimerId, `${elapsedTime}`);
      const sessionTimeoutWarning = (sessionTimeoutMinutes - 5) * 60;
      const sessionTimeout = sessionTimeoutMinutes * 60 - 1; // - 1 sec to avoid overlapping with a background request

      if (elapsedTime === sessionTimeoutWarning) {
        showAlert("You are about to be logged out due to inactivity.", dispatch);
      }

      if (elapsedTime === sessionTimeout) {
        dispatch(logOut());
        showAlert(
          `You have been logged out due to ${sessionTimeoutMinutes} minutes of inactivity`,
          dispatch,
        );
      }
    }, 1000);

    dispatch({
      sessionTimer: sessionTimer,
      sessionTimerId: sessionTimerId,
      type: actionTypes.SET_SESSION_TIMER,
    });

    for (let event of events) {
      window.addEventListener(event, resetSessionTimeout);
    }
  };

export const loadToken = () => (dispatch) => {
  let idToken = window.localStorage.getItem("token");
  let refreshToken = window.localStorage.getItem("refreshToken");
  let accessToken = window.localStorage.getItem("accessToken");
  let role = null;

  if (idToken) {
    let decodedToken = jwt_decode(idToken);

    role = decodedToken["cognito:groups"][0];
    const redirectPath = ROLES_CONF[role].routeBase;

    dispatch(getToken(idToken, refreshToken, accessToken, redirectPath, role));
    setSessionTimeout(idToken, dispatch, role);
  } else {
    return;
  }
};

export const getPatientProfile =
  (showLoader = false) =>
  (dispatch) => {
    return authRequests
      .fetch_profile({ context: { showLoader } })
      .then((cleanResponse) => {
        dispatch(profileFetchSuccess(cleanResponse.user));
      })
      .catch(() => {
        dispatch(profileFetchFail());
      });
  };

export const getProviderProfile = (providerId) => (dispatch) => {
  const searchParams = { providerId };

  providerRequests
    .fetch_provider_profile({ context: { showLoader: false }, searchParams })
    .then((cleanResponse) => {
      dispatch(profileFetchSuccess(cleanResponse.user));
    })
    .catch(() => {
      dispatch(profileFetchFail());
    });
};

export const updateReduxProfile = (profile) => (dispatch) => {
  dispatch(profileFetchSuccess(profile));
};

export const removeTaskFromProfile = (taskId) => (dispatch, getState) => {
  const currentProfile = getState().auth.profile;

  if (!currentProfile || !currentProfile.tasks) return;

  const updatedProfile = {
    ...currentProfile,
    tasks: currentProfile.tasks.filter((task) => task.task_id !== taskId),
  };

  dispatch(profileFetchSuccess(updatedProfile));
};

export const updateProviderProfile = (data, providerId) => (dispatch) => {
  const searchParams = { providerId: providerId };

  providerRequests
    .update_profile({ data, searchParams })
    .then(() => {
      dispatch(profileFetchSuccess(data));
    })
    .catch((err) => {
      console.log(err);
    });
};

export const getAdminProfile = () => (dispatch) => {
  const context = { showLoader: false };

  adminRequests
    .fetch_admin_profile({ context })
    .then((cleanResponse) => {
      dispatch(profileFetchSuccess(cleanResponse));
    })
    .catch(() => {
      dispatch(profileFetchFail());
    });
};

function doAuthentication(cleanResponse, dispatch) {
  let role = null;

  if (isEmpty(cleanResponse["ChallengeParameters"])) {
    let idToken = cleanResponse.AuthenticationResult.IdToken;
    let refreshToken = cleanResponse.AuthenticationResult.RefreshToken;
    let accessToken = cleanResponse.AuthenticationResult.AccessToken;
    let decodedToken = jwt_decode(idToken);

    role = decodedToken["cognito:groups"][0];
    const redirectPath = ROLES_CONF[role].routeBase;
    const days_until_reset = cleanResponse.days_until_reset;

    dispatch(authSuccess(idToken, refreshToken, accessToken, redirectPath, role, days_until_reset));
    setSessionTimeout(idToken, dispatch, role);
  } else {
    dispatch(authChallenge(cleanResponse));
  }
}

const getSessionTimeout = async (idToken) => {
  try {
    const response = await authRequests.config(idToken);

    return response?.session_timeout || DEFAULT_SESSION_TIMEOUT;
  } catch (e) {
    console.log(e);

    return DEFAULT_SESSION_TIMEOUT;
  }
};

const setSessionTimeout = (idToken, dispatch, role) => {
  if (isProvider(role)) {
    getSessionTimeout(idToken)
      .then((sessionTimeout) => {
        dispatch(initSessionTimeout(sessionTimeout || DEFAULT_SESSION_TIMEOUT));
      })
      .catch((err) => {
        console.log(err);
        dispatch(initSessionTimeout(DEFAULT_SESSION_TIMEOUT));
      });
  } else {
    dispatch(initSessionTimeout(DEFAULT_SESSION_TIMEOUT));
  }
};

export const logIn = (creds) => (dispatch) => {
  return authRequests
    .login({ data: creds })
    .then((cleanResponse) => {
      if (
        Number(cleanResponse.days_until_reset) <= 0 &&
        isEmpty(cleanResponse["ChallengeParameters"])
      ) {
        dispatch(pwResetRequired(cleanResponse));
      } else {
        doAuthentication(cleanResponse, dispatch);
      }
    })
    .catch((err) => {
      dispatch(authFail(parseErrorMessage(err), err.cleanError?.type));
      showAlert(parseErrorMessage(err), dispatch);
    });
};

export const authChallengeRequest = (formData) => (dispatch) => {
  authRequests
    .auth_challenge({ data: formData })
    .then((cleanResponse) => {
      doAuthentication(cleanResponse, dispatch);
    })
    .catch((err) => {
      dispatch(authFail(parseErrorMessage(err)));
      showAlert(parseErrorMessage(err), dispatch);
    });
};

export const clearAuthChallenge = () => (dispatch) => {
  dispatch({
    type: actionTypes.CLEAR_AUTH_CHALLENGE,
  });
};

export const completeAutoSignUp = (formData) => (dispatch) => {
  authRequests
    .complete_auto_signup({ data: formData })
    .then((cleanResponse) => {
      doAuthentication(cleanResponse, dispatch);
    })
    .catch((err) => {
      dispatch(authFail(parseErrorMessage(err)));
      showAlert(parseErrorMessage(err), dispatch);
    });
};

export const workflowSetPassword =
  ({ data }) =>
  (dispatch) => {
    sharedRequests
      .set_password({ data })
      .then((cleanResponse) => {
        const auth_data = cleanResponse.auth_data;

        doAuthentication(auth_data, dispatch);
      })
      .catch((err) => {
        dispatch(authFail(parseErrorMessage(err)));
        showAlert(parseErrorMessage(err), dispatch);
      });
  };

export const signUp = (formData) => (dispatch) => {
  authRequests
    .sign_up({ data: formData })
    .then(() => {
      dispatch(signUpSuccess());
      dispatch(
        logIn({
          PASSWORD: formData.Password,
          USERNAME: formData.Username,
        }),
      );
    })
    .catch((err) => {
      dispatch(signUpFail());
      showAlert(parseErrorMessage(err), dispatch);
    });
};

export const showEmailVerified = (showEmailVerified) => (dispatch) => {
  if (showEmailVerified) {
    showAlert("Email Address Verified. Please Login", dispatch, TOAST_SUCCESS_2);
  }
  dispatch({
    showEmailVerified: showEmailVerified,
    type: actionTypes.SHOW_EMAIL_VERIFIED,
  });
};

export const showSuccessfulSubmission = () => (dispatch) => {
  showAlert(
    "Thanks, you will be notified when your child becomes eligible.",
    dispatch,
    TOAST_SUCCESS_2,
  );
};

export const mailResetPasswordCode = (email) => (dispatch) => {
  authRequests
    .mail_forgot_pw_code({ data: { Username: email } })
    .then(() => {
      dispatch({ payload: email, type: actionTypes.RS_PW_CODE_SENT });
      showAlert(
        "Please check your email and follow the instructions to reset your password.",
        dispatch,
        TOAST_ALERT_2,
      );
    })
    .catch((err) => {
      showAlert(parseErrorMessage(err), dispatch);
    });
};

export const alreadyHaveACode = (email) => (dispatch) => {
  dispatch({ payload: email, type: actionTypes.RS_PW_CODE_SENT });
};

export const resetForgotPwStep = () => (dispatch) => {
  dispatch({ type: actionTypes.RS_FORGOT_PW_STEPS });
};
export const resetPw = (requestObject, cb) => async (dispatch) => {
  try {
    await authRequests.reset_forgotten_pw({ data: requestObject });
    showAlert(
      "You've successfully reset your password! Log in with your credentials below.",
      dispatch,
      TOAST_ALERT_2,
    );
    cb(true);
  } catch (err) {
    showAlert(parseErrorMessage(err), dispatch);
    cb(false);
  }
};

export const changePassword = (requestObject, cb) => async (dispatch) => {
  try {
    await authRequests.change_password({ data: requestObject });
    cb(true);
  } catch (err) {
    showAlert(parseErrorMessage(err), dispatch);
    cb(false);
  }
};

export const setAuthAfterPasswordReset =
  (requestObject, accessToken, cleanResponse) => (dispatch) => {
    authRequests
      .change_password({ accessToken, data: requestObject })
      .then(() => {
        doAuthentication(cleanResponse, dispatch);
      })
      .catch((err) => {
        showAlert(parseErrorMessage(err), dispatch);
      });
  };

export const hideRsPwSuccess = () => (dispatch) => {
  dispatch({ type: actionTypes.HIDE_RS_PW_SUCCESS });
};

export const validateReferral = (referralCode, cb) => async (dispatch) => {
  try {
    const cleanResponse = await authRequests.validate_referral({ data: { referralCode } });

    if (cleanResponse) {
      dispatch(referralSuccess(cleanResponse.user));
      cb();
    } else {
      dispatch(referralFail());
    }
  } catch (err) {
    dispatch(referralFail());
    showAlert(parseErrorMessage(err), dispatch);
  }
};

export const uploadPatientInsuranceImage = (imgData, attr, cb, entityId) => (dispatch) => {
  const { file, fileName } = imgData;

  const onSuccessCallback = (base64Img) => {
    showAlert("Image uploaded", dispatch, TOAST_SUCCESS);
    switch (attr) {
      case "pb":
        dispatch({
          payload: { preview: base64Img, url: fileName },
          type: actionTypes.SET_BACK_PRI_INS_IMAGE,
        });
        break;

      case "pf":
        dispatch({
          payload: { preview: base64Img, url: fileName },
          type: actionTypes.SET_FRONT_PRI_INS_IMAGE,
        });
        break;

      case "sb":
        dispatch({
          payload: { preview: base64Img, url: fileName },
          type: actionTypes.SET_BACK_SEC_INS_IMAGE,
        });
        break;

      case "sf":
        dispatch({
          payload: { preview: base64Img, url: fileName },
          type: actionTypes.SET_FRONT_SEC_INS_IMAGE,
        });
        break;

      default:
        dispatch({
          payload: { preview: base64Img, url: fileName },
          type: actionTypes.SET_FRONT_PRI_INS_IMAGE,
        });
    }
    cb(attr);
  };

  try {
    uploadInsuranceImageFile(file, fileName, entityId, UserFileTypes.PATIENT_INSURANCE_IMAGE)
      .then(onSuccessCallback)
      .catch((err) => {
        showAlert(parseErrorMessage(err), dispatch);
      });
  } catch (err) {
    showAlert(parseErrorMessage(err), dispatch);
  }
};

export const unsetInsImage = (attr) => (dispatch) => {
  switch (attr) {
    case "pb":
      dispatch({ type: actionTypes.UNSET_BACK_PRI_INS_IMAGE });
      break;

    case "pf":
      dispatch({ type: actionTypes.UNSET_FRONT_PRI_INS_IMAGE });
      break;

    case "pri":
      dispatch({ type: actionTypes.UNSET_PRI_INS_IMAGES });
      break;

    case "sb":
      dispatch({ type: actionTypes.UNSET_BACK_SEC_INS_IMAGE });
      break;

    case "sec":
      dispatch({ type: actionTypes.UNSET_SEC_INS_IMAGES });
      break;

    case "sf":
      dispatch({ type: actionTypes.UNSET_FRONT_SEC_INS_IMAGE });
      break;

    default:
      return;
  }
};

export const logInWithExternalProvider = (data) => (dispatch) => {
  authRequests
    .custom_auth({ data })
    .then((cleanResponse) => {
      doAuthentication(cleanResponse, dispatch);
    })
    .catch((err) => {
      dispatch(authFail(parseErrorMessage(err)));
      showAlert(parseErrorMessage(err), dispatch);
    });
};
